import React, { useState, useEffect, useCallback } from 'react';
import {
  listWorkflowDefinitions,
  getTemplateCounts,
  archiveWorkflowDefinition,
  restoreWorkflowDefinition,
  permanentDeleteWorkflowDefinition,
  type WorkflowDefinitionWithStats,
  type WorkflowListFilters,
  type WorkflowStatus,
  type CitationStats,
  STATUS_LABELS,
  STATUS_COLORS,
} from './workflowDefinitionService';
import ConfirmActionModal from './ConfirmActionModal';
import { PracticeAreaConfigService } from '../../../../../services/practiceAreaConfigService';

interface Props {
  onEditDefinition: (id: string) => void;
  onCreateNew: () => void;
}

const STATUS_OPTIONS: WorkflowStatus[] = ['published', 'validated', 'under_review', 'draft', 'archived'];

// US States for the dropdown — firm's licensed states shown first
const ALL_US_STATES: Array<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'DC', name: 'District of Columbia' },
];

// ─── Health Badge ───

function getHealthInfo(stats?: CitationStats): { label: string; color: string; dotColor: string; tooltip: string } {
  if (!stats || stats.total_tasks === 0) {
    return { label: 'No tasks', color: 'text-gray-400 dark:text-gray-500', dotColor: 'bg-gray-300 dark:bg-gray-600', tooltip: 'This definition has no tasks yet' };
  }

  const citedPct = Math.round((stats.cited_tasks / stats.total_tasks) * 100);

  // Check mandated tasks specifically
  if (stats.mandated_tasks > 0 && stats.mandated_cited < stats.mandated_tasks) {
    const gap = stats.mandated_tasks - stats.mandated_cited;
    return {
      label: `${stats.cited_tasks}/${stats.total_tasks} cited`,
      color: 'text-rose-600 dark:text-rose-400',
      dotColor: 'bg-rose-500',
      tooltip: `${gap} court-mandated task${gap !== 1 ? 's' : ''} missing statute citation — compliance risk`,
    };
  }

  if (citedPct === 100) {
    return {
      label: `${stats.total_tasks}/${stats.total_tasks} cited`,
      color: 'text-emerald-600 dark:text-emerald-400',
      dotColor: 'bg-emerald-500',
      tooltip: 'All tasks have statute citations — fully compliant',
    };
  }

  if (citedPct >= 80) {
    return {
      label: `${stats.cited_tasks}/${stats.total_tasks} cited`,
      color: 'text-amber-600 dark:text-amber-400',
      dotColor: 'bg-amber-500',
      tooltip: `${stats.total_tasks - stats.cited_tasks} task${stats.total_tasks - stats.cited_tasks !== 1 ? 's' : ''} without citation — consider adding references`,
    };
  }

  return {
    label: `${stats.cited_tasks}/${stats.total_tasks} cited`,
    color: 'text-rose-600 dark:text-rose-400',
    dotColor: 'bg-rose-500',
    tooltip: `Only ${citedPct}% of tasks have citations — significant gaps remain`,
  };
}

// ─── Component ───

export const WorkflowDefinitionBrowser: React.FC<Props> = ({ onEditDefinition, onCreateNew }) => {
  const [definitions, setDefinitions] = useState<WorkflowDefinitionWithStats[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<WorkflowListFilters>({ page: 1, limit: 20 });
  const [templateCounts, setTemplateCounts] = useState<Record<string, number>>({});

  // Archive / restore / permanent delete modal
  const [archiveTarget, setArchiveTarget] = useState<WorkflowDefinitionWithStats | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<WorkflowDefinitionWithStats | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkflowDefinitionWithStats | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Firm-scoped filter options
  const [firmStates, setFirmStates] = useState<string[]>([]);
  const [practiceAreas, setPracticeAreas] = useState<Array<{ name: string; displayName: string }>>([]);

  // Load firm's practice areas and states
  useEffect(() => {
    let mounted = true;
    const loadFirmData = async () => {
      try {
        // Load practice areas
        const paService = PracticeAreaConfigService.getInstance();
        const available = await paService.getAvailablePracticeAreas();
        if (!mounted) return;
        setPracticeAreas(
          available
            .filter((a) => a.isActive)
            .map((a) => ({ name: a.name, displayName: a.displayName || a.name })),
        );

        // Load firm settings for licensed states
        try {
          const firmSettings = await paService.getFirmSettings();
          if (mounted && firmSettings?.licensedStates?.length) {
            setFirmStates(firmSettings.licensedStates);
          }
        } catch {
          // Firm settings may not be available — fall back to all states
        }
      } catch (err) {
        console.error('Failed to load firm data for workflow browser:', err);
      }
    };
    loadFirmData();
    return () => { mounted = false; };
  }, []);

  const loadDefinitions = useCallback(async () => {
    let mounted = true;
    try {
      setLoading(true);
      setError(null);
      const result = await listWorkflowDefinitions(filters);
      if (!mounted) return;
      setDefinitions(result.items);
      setTotal(result.total);

      // Load template counts (non-blocking)
      if (result.items.length > 0) {
        getTemplateCounts(
          result.items.map((d) => ({ id: d.id, state_code: d.state_code, practice_area: d.practice_area, case_subtype: d.case_subtype })),
        ).then((counts) => { if (mounted) setTemplateCounts(counts); })
         .catch(() => {});
      }
    } catch (err) {
      if (!mounted) return;
      setError(err instanceof Error ? err.message : 'Failed to load workflow definitions');
    } finally {
      if (mounted) setLoading(false);
    }
    return () => { mounted = false; };
  }, [filters]);

  useEffect(() => {
    loadDefinitions();
  }, [loadDefinitions]);

  const updateFilter = (key: keyof WorkflowListFilters, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));
  };

  const totalPages = Math.ceil(total / (filters.limit || 20));

  // Build state dropdown options — firm states first, then all others
  const stateOptions = (() => {
    if (firmStates.length === 0) return ALL_US_STATES;
    const firmSet = new Set(firmStates);
    const firm = ALL_US_STATES.filter((s) => firmSet.has(s.code));
    const other = ALL_US_STATES.filter((s) => !firmSet.has(s.code));
    return [...firm, { code: '---', name: '───────────────' }, ...other];
  })();

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      setArchiving(true);
      await archiveWorkflowDefinition(archiveTarget.id);
      setArchiveTarget(null);
      loadDefinitions();
    } catch (err) {
      console.error('Failed to archive workflow definition:', err);
      setError(err instanceof Error ? err.message : 'Failed to archive workflow definition');
      setArchiveTarget(null);
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    try {
      setRestoring(true);
      await restoreWorkflowDefinition(restoreTarget.id);
      setRestoreTarget(null);
      loadDefinitions();
    } catch (err) {
      console.error('Failed to restore workflow definition:', err);
      setError(err instanceof Error ? err.message : 'Failed to restore workflow definition');
      setRestoreTarget(null);
    } finally {
      setRestoring(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await permanentDeleteWorkflowDefinition(deleteTarget.id);
      setDeleteTarget(null);
      loadDefinitions();
    } catch (err) {
      console.error('Failed to permanently delete workflow definition:', err);
      setError(err instanceof Error ? err.message : 'Failed to permanently delete workflow definition');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const selectClasses = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-lex-accent transition-colors';

  return (
    <div className="space-y-4">
      {/* ─── Filter Bar ─── */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">State</label>
          <select
            value={filters.state_code || ''}
            onChange={(e) => updateFilter('state_code', e.target.value)}
            className={selectClasses}
          >
            <option value="">All States</option>
            {firmStates.length > 0 && <option disabled className="text-gray-400">── Licensed States ──</option>}
            {stateOptions.map((s) =>
              s.code === '---' ? (
                <option key="sep" disabled className="text-gray-400">{s.name}</option>
              ) : (
                <option key={s.code} value={s.code}>
                  {s.code} — {s.name}{firmStates.includes(s.code) ? ' ★' : ''}
                </option>
              ),
            )}
          </select>
        </div>

        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Practice Area</label>
          <select
            value={filters.practice_area || ''}
            onChange={(e) => updateFilter('practice_area', e.target.value)}
            className={selectClasses}
          >
            <option value="">All Practice Areas</option>
            {practiceAreas.map((pa) => (
              <option key={pa.name} value={pa.name}>
                {pa.displayName}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
          <select
            value={filters.status || ''}
            onChange={(e) => updateFilter('status', e.target.value)}
            className={selectClasses}
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div>
          <button
            onClick={onCreateNew}
            className="px-4 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent hover:bg-lex-accent-hover rounded-lg shadow-sm transition-colors whitespace-nowrap"
          >
            + New Workflow
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-lex-accent border-t-transparent" />
        </div>
      )}

      {/* Empty state */}
      {!loading && definitions.length === 0 && !error && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No workflow definitions</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Get started by creating a new workflow definition or running the database seed.
          </p>
        </div>
      )}

      {/* ─── Definition Cards ─── */}
      {!loading && definitions.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {definitions.map((def) => {
            const statusColor = STATUS_COLORS[def.status] || STATUS_COLORS.draft;
            const health = getHealthInfo(def.citation_stats);
            const tplCount = templateCounts[def.id] || 0;

            return (
              <button
                key={def.id}
                onClick={() => onEditDefinition(def.id)}
                className="text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:shadow-lg hover:border-lex-accent dark:hover:border-lex-accent transition-all group"
              >
                {/* Header: Title + Status */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 group-hover:text-lex-accent dark:group-hover:text-lex-accent transition-colors">
                    {def.label}
                  </h3>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusColor.bg} ${statusColor.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusColor.dot}`} />
                    {STATUS_LABELS[def.status]}
                  </span>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-lex-accent-soft dark:bg-lex-accent-soft text-lex-accent dark:text-lex-accent">
                    {def.state_code}
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">
                    {def.practice_area.replace(/_/g, ' ')}
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {def.case_subtype.replace(/_/g, ' ')}
                  </span>
                </div>

                {/* Description */}
                {def.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
                    {def.description}
                  </p>
                )}

                {/* ─── Health Badge ─── */}
                {def.citation_stats && def.citation_stats.total_tasks > 0 && (
                  <div className="mb-3 flex items-center gap-2" title={health.tooltip}>
                    <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          def.citation_stats.cited_tasks === def.citation_stats.total_tasks
                            ? 'bg-emerald-500'
                            : def.citation_stats.mandated_tasks > 0 && def.citation_stats.mandated_cited < def.citation_stats.mandated_tasks
                              ? 'bg-rose-500'
                              : 'bg-amber-500'
                        }`}
                        style={{ width: `${Math.round((def.citation_stats.cited_tasks / def.citation_stats.total_tasks) * 100)}%` }}
                      />
                    </div>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium whitespace-nowrap ${health.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${health.dotColor}`} />
                      {health.label}
                    </span>
                  </div>
                )}

                {/* Footer: Version, authority, template count, archive */}
                <div className="flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
                  <span>v{def.version}</span>
                  {def.confidence_score != null && (
                    <span>{(def.confidence_score * 100).toFixed(0)}%</span>
                  )}
                  {def.source_authority && (
                    <span className="truncate" title={def.source_authority}>
                      {def.source_authority}
                    </span>
                  )}
                  {tplCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-lex-accent dark:text-lex-accent" title={`${tplCount} synthesized template(s) use this definition`}>
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                      </svg>
                      {tplCount}
                    </span>
                  )}
                  {def.status !== 'archived' ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); setArchiveTarget(def); }}
                      className="ml-auto p-1 rounded opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                      title="Archive this definition"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
                      </svg>
                    </button>
                  ) : (
                    <span className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button
                        onClick={(e) => { e.stopPropagation(); setRestoreTarget(def); }}
                        className="p-1 rounded text-gray-400 hover:text-lex-accent dark:hover:text-lex-accent hover:bg-lex-state-hover dark:hover:bg-lex-state-hover transition-all"
                        title="Restore to Draft"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(def); }}
                        className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                        title="Permanently delete"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                      </button>
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {total} definition{total !== 1 ? 's' : ''} total
          </span>
          <div className="flex gap-2">
            <button
              disabled={filters.page === 1}
              onClick={() => setFilters((p) => ({ ...p, page: (p.page || 1) - 1 }))}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              Previous
            </button>
            <span className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400">
              Page {filters.page || 1} of {totalPages}
            </span>
            <button
              disabled={(filters.page || 1) >= totalPages}
              onClick={() => setFilters((p) => ({ ...p, page: (p.page || 1) + 1 }))}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Archive Confirm Modal */}
      {archiveTarget && (
        <ConfirmActionModal
          title="Archive Workflow Definition"
          message={`Archive "${archiveTarget.label}"? Archived definitions are hidden from active views but can still be found under the Archived filter.`}
          confirmLabel="Archive"
          variant="danger"
          loading={archiving}
          onConfirm={handleArchive}
          onCancel={() => setArchiveTarget(null)}
        />
      )}

      {/* Restore Confirm Modal */}
      {restoreTarget && (
        <ConfirmActionModal
          title="Restore Workflow Definition"
          message={`Restore "${restoreTarget.label}" from archive? It will be returned to Draft status.`}
          confirmLabel="Restore to Draft"
          variant="info"
          loading={restoring}
          onConfirm={handleRestore}
          onCancel={() => setRestoreTarget(null)}
        />
      )}

      {/* Permanent Delete Confirm Modal */}
      {deleteTarget && (
        <ConfirmActionModal
          title="Permanently Delete"
          message={`Permanently delete "${deleteTarget.label}"? This will remove all phases, tasks, and form links. This action cannot be undone.`}
          confirmLabel="Delete Forever"
          variant="danger"
          loading={deleting}
          onConfirm={handlePermanentDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};
